import { useCallback } from 'react';

type UseItemStringConvertersProps<Item> = {
  getLabel?: (item: Item) => string;
  getStringValue?: (item: Item) => string;
};

type UseItemStringConvertersReturn<Item> = {
  itemToStringLabel: (item: Item | null | undefined) => string;
  itemToStringValue: (item: Item | null | undefined) => string;
};

/**
 * Hook that provides memoized functions for converting items to string labels and values.
 * Handles null/undefined items gracefully by returning empty strings.
 *
 * @param getLabel - Optional function to extract display label from an item
 * @param getStringValue - Optional function to extract string value from an item
 * @returns Object containing itemToStringLabel and itemToStringValue functions
 */
export const useItemStringConverters = <Item>({
  getLabel,
  getStringValue,
}: UseItemStringConvertersProps<Item>): UseItemStringConvertersReturn<Item> => {
  const itemToStringLabel = useCallback(
    (item: Item | null | undefined): string => {
      if (item == null) return '';
      if (getLabel) return getLabel(item);
      return String(item);
    },
    [getLabel]
  );

  const itemToStringValue = useCallback(
    (item: Item | null | undefined): string => {
      if (item == null) return '';
      if (getStringValue) return getStringValue(item);
      return String(item);
    },
    [getStringValue]
  );

  return {
    itemToStringLabel,
    itemToStringValue,
  };
};
